import React, {
  ForwardedRef,
  useImperativeHandle,
  useMemo,
  useRef,
  useCallback,
  useState,
} from 'react';
import classNames from 'classnames';
import { cloneDeep } from 'lodash-es';
import { ChartOption, ChartRef, BarChartProps } from '../_config/_interface';
import { useChartYaxisRange } from '../_config/_hooks';
import { adjustChartMargin, setDataLabel, setXLabelAlign } from '@customComp/Chart/chartConfigs/plugs';
import { formatDisplayValue } from '@customComp/Chart/_config/_utils';
import { useBarOption } from '@customComp/Chart/chartConfigs/barChartHooks';
import { DATALAB_SPLIT_STR } from '@dbl/core/global';
import { setTooltipMaxSize } from '@customComp/Chart/chartConfigs/tooltip/config';
import { changeLegendLocation } from '@customComp/Chart/chartConfigs/legend/legendUtils';
import Chart from '..';
import TextBubbles from '@home/components/TextBubbles';

const BarChartInner: React.ForwardRefRenderFunction<ChartRef, BarChartProps> = (
  props,
  ref: ForwardedRef<ChartRef>,
) => {
  const {
    chartData,
    metricNames,
    isOnlyOneDimension,
    chartConfig,
    fieldInfos,
    compareDateCount,
    metricIsAllPercent,
    userFilterControls,
    setOtherConfigFunc,
    onEvents: externalOnEvents,
    tooltipMinTopPx,
  } = props;
  const chartRef = useRef<ChartRef>(null);
  const [ecInstance, setEcInstance] = useState(chartRef?.current?.instance());
  const [chartLayoutRect, setChartLayoutRect] = useState<{
    width: number;
    height: number;
    gridWidth: number;
    gridHeight: number;
  }>({ width: 0, height: 0, gridWidth: 0, gridHeight: 0 });
  const instance = useCallback(
    () => chartRef?.current?.instance(),
    [chartRef],
  );
  const { yAxisName } = chartConfig || {};
  const hasYAxisName = Boolean(yAxisName);
  useImperativeHandle(ref, () => ({
    ecInstance,
    instance,
  }));

  const displayConfig = useMemo(() => {
    const config: Record<string, { accuracy: number; unit: string; }> = {};
    metricNames.forEach((metricName: string) => {
      const { accuracy, unit } = fieldInfos?.find((fieldInfo: { name: string; }) => fieldInfo.name === metricName) || { accuracy: 0, unit: 'bmk' };
      config[metricName] = { accuracy, unit };
    });
    return config;
  }, [fieldInfos, metricNames]);

  let option = useBarOption({
    ...chartData,
    ecInstance,
    compareDateCount,
    metricIsAllPercent,
    metricNames,
    isOnlyOneDimension,
    tooltipMinTopPx,
    fieldInfos,
    userFilterControls,
    chartConfig,
    setOtherConfigFunc: (opt) => {
      let option = cloneDeep(opt);
      // 设置数据标签
      option = setDataLabel(option, chartConfig, {
        metricFormatter: (param, isPercent) => {
          const { accuracy, unit } = displayConfig[param?.data?.metric || ''] || { accuracy: 0, unit: 'bmk' };
          return formatDisplayValue({
            value: param.value,
            isPercent,
            accuracy,
            unit,
          });
        },
        dimensionFormatter: (params) => {
          if (isOnlyOneDimension) return '';
          const { seriesName = '', data = {} } = params || {};
          if (data?.isCompare) {
            return seriesName.split(DATALAB_SPLIT_STR)[0];
          }
          return seriesName;
        },
      });
      // 设置tooltip最大尺寸
      option = setTooltipMaxSize(option, chartConfig, { chartLayoutRect });
      // 设置图表边距
      adjustChartMargin(option, chartConfig, {
        ecInstance: instance?.(),
        topAuto: chartConfig?.showDataLabel,
        rightAuto: chartConfig?.showDataLabel,
      });
      // 设置图例位置
      if (chartConfig?.legendPosition) {
        changeLegendLocation({
          option,
          position: chartConfig.legendPosition,
        });
      }
      return setOtherConfigFunc ? setOtherConfigFunc?.(option) : option;
    },
  });

  const handleChartRendered = useCallback((): void => {
    const chartInstance = instance?.() as any;
    setEcInstance((prev) => {
      if (prev === chartInstance) {
        return prev;
      }
      return chartInstance;
    });
    const instanceRect = chartInstance?.getDom()?.getBoundingClientRect?.();
    const width = instanceRect?.width ?? 400;
    const height = instanceRect?.height ?? 379;
    const gridRect = chartInstance?.getModel?.()?.getComponent?.('grid', 0)
      ?.coordinateSystem?.getRect?.();
    const gridWidth = gridRect?.width ?? 400;
    const gridHeight = gridRect?.height ?? 379;
    setChartLayoutRect((prev) => {
      // 图表宽高不变时保持 state 引用不变，避免重渲染 → Chart setOption → rendered 死循环
      // 这里只比较图表宽高，因为绘图区宽高可能随着数据变化而变化
      if (prev.width === width && prev.height === height) {
        return prev;
      }
      return { width, height, gridWidth, gridHeight };
    });
  }, [instance]);

  // 监听图表渲染事件，拿到最新绘图区高度
  const onEvents = useMemo(() => {
    const events: Record<string, (event: any, chart: any) => void> = {
      rendered: handleChartRendered,
      finished: handleChartRendered,
      ...(externalOnEvents ?? {}),
    };
    return events;
  }, [handleChartRendered]);

  option = setXLabelAlign(option as any, { type: chartConfig.xLabel }) as ChartOption;
  // y轴range调整
  const rangeOption = useChartYaxisRange({ option });

  if (chartConfig?.yAxisRangeSwitch) {
    option = rangeOption as ChartOption;
  }

  return (
    <div className="analysis-chart-container">
      <div
        className={classNames('dbl-charts-top-yAxis-text', {
          'dbl-charts-top-yAxis-text--hidden': !hasYAxisName,
        })}
      >
        <div className="dbh-text-exceeds-bubble-left">
          <TextBubbles text={yAxisName} />
        </div>
      </div>
      <div className="analysis-chart-content">
        <Chart ref={chartRef} option={option} notMerge={true} onEvents={onEvents} />
      </div>
    </div>
  );
};

const BarChart = React.forwardRef(BarChartInner);

export default BarChart;
